import { union } from 'lodash'
import { EmploymentTextType } from '../../../components'
import { isAddressLocationDomestic } from '../../../pages/own-info/tabs/base-details/utils'
import {
  capitalizeName,
  formatBasicDateStr,
  isAfterDate,
  isArrayWithLength,
  isBeforeDate,
  MaybeAddress,
  MaybeEmail,
  MaybePhone,
  MaybeString
} from '../../../utils'
import {
  AddressType,
  Contacts,
  EmploymentSummary,
  EmploymentSummaryInfo,
  Maybe,
  MembershipFeeObligations,
  SelfPaymentObligation,
  SettlementObligation
} from '../../../utils/classes/graphql'

export const formatUserName = (
  etunimet: MaybeString,
  sukunimi: MaybeString
) => {
  const formattedName =
    etunimet || sukunimi ? capitalizeName(`${etunimet} ${sukunimi}`) : ''
  return formattedName
}
export const formatContactList = (
  userAddressList: Contacts['osoitteet'],
  userPhoneList: Contacts['puhelinnumerot'],
  userEmailList: Contacts['sahkopostit']
) => {
  const formattedEmails = userEmailList?.map((item: MaybeEmail) => {
    return item?.sahkopostiosoite
  })

  const formattedPhones = userPhoneList?.map((item: MaybePhone) => {
    const phoneNumber = item?.puhelinnumero
    const shortenedNumber = phoneNumber ? phoneNumber.substring(0, 25) : ''
    return shortenedNumber
  })

  const formattedAddresses = userAddressList
    ?.filter((address) => address?.tyyppi === AddressType.Kotiosoite)
    .map((homeAddress: MaybeAddress) => {
      const streetAddress = `${capitalizeName(
        homeAddress?.katuosoite?.toLowerCase()
      )}, ${homeAddress?.postinumero} ${homeAddress?.postitoimipaikka}`
      const country = !isAddressLocationDomestic(homeAddress?.maaKoodi)
        ? `, ${homeAddress?.maa}`
        : ''
      const userAddress = streetAddress + country
      return userAddress
    })

  const contactInfoList = union(
    formattedAddresses,
    formattedPhones,
    formattedEmails
  )
  return contactInfoList
}

const hasValidDates = (startDateStr: string, endDateStr: string) => {
  const currentDate = new Date()
  const hasStarted =
    isAfterDate(currentDate, startDateStr) || startDateStr === null
  const hasEnded = isBeforeDate(currentDate, endDateStr)

  return hasStarted && !hasEnded
}

/**
 * There can be multiple valid employments
 * @param employments
 * @returns list containing all valid employments
 */
export const filterValidEmployment = (employments: EmploymentSummaryInfo) => {
  const initialArr: EmploymentSummaryInfo['tyosuhteet'] = employments.tyosuhteet
  const isValid = (employment: Maybe<EmploymentSummary>) => {
    if (hasValidDates(employment?.alkupvm, employment?.loppupvm)) {
      return employment
    }
  }

  const filteredArr = initialArr.filter(isValid)

  const employmentsArr: EmploymentTextType[] = filteredArr.map(
    (employment: EmploymentSummary) => {
      const formattedStartDate = formatBasicDateStr(employment?.alkupvm) || ''
      const formattedEndDate = formatBasicDateStr(employment?.loppupvm) || ''
      const hyphen = formattedStartDate || formattedEndDate ? ' - ' : ''
      const formattedName = capitalizeName(employment?.taNimi)
      const text =
        formattedName + ' ' + formattedStartDate + hyphen + formattedEndDate
      return {
        employmentText: text
      }
    }
  )
  return employmentsArr
}

export const filterValidObligations = (
  membershipFeeObligations: Maybe<MembershipFeeObligations>
) => {
  const selfpayments = membershipFeeObligations?.selfPaymentObligations
  const settlements = membershipFeeObligations?.settlementObligations

  const isValid = (
    item: Maybe<SettlementObligation> | Maybe<SelfPaymentObligation>
  ) => {
    if (hasValidDates(item?.aloituspvm, item?.lopetuspvm)) {
      return item
    }
  }

  const filteredSettlements = settlements ? settlements.filter(isValid) : []
  const filteredSelfpayments = selfpayments ? selfpayments.filter(isValid) : []

  const paymentMethods = (
    filteredSettlements: Maybe<Array<Maybe<SettlementObligation>>>,
    filteredSelfpayments: Maybe<Array<Maybe<SelfPaymentObligation>>>
  ) => {
    const hasSettlements = isArrayWithLength(filteredSettlements)
    const hasSelfpayments = isArrayWithLength(filteredSelfpayments)

    if (hasSettlements && hasSelfpayments) {
      return 'pages.membershipFeesPage.paymentInformation.paymentMethods.bothMethods'
    } else if (hasSettlements && !hasSelfpayments) {
      return 'pages.membershipFeesPage.paymentInformation.paymentMethods.settlement'
    } else if (!hasSettlements && hasSelfpayments) {
      return 'pages.membershipFeesPage.paymentInformation.paymentMethods.selfpayment'
    } else {
      return 'pages.membershipFeesPage.paymentInformation.paymentMethods.noMethod'
    }
  }

  const paymentMethod = paymentMethods(
    filteredSettlements,
    filteredSelfpayments
  )

  return paymentMethod
}
